import React, {useEffect} from "react";
import {
    ActionType,
    Column,
    ColumnType,
    FixedSelectionFilter,
    FreeTextFilter,
    GetParams,
    IMAGES,
    KButton,
    KBUTTON_SIZE,
    KBUTTON_VARIANT,
    KCard,
    KContainer,
    KModal,
    KModalSize,
    KModalVariant,
    KSpace,
    KSpinner,
    KSpinnerSize,
    KTableLoader,
    Table
} from "@kopjra/uikit";
import {Credential, ProductCredits} from "../types/users";
import {I18n, Localize, Translate} from "react-redux-i18n";
import {tableGetParams, tableHasFilter} from "../utils/commons";
import {Alert, Col, Row} from "react-bootstrap";
import {HtmlSignature} from "../types/htmlSignatures";
import {
    AppStatus,
    AppStatusToColorMap,
    AppStatusToIconMap,
    AppStatusToInteractionStatusMap,
    InteractionStatus,
    InteractionStatusAppStatusMap
} from "../types/signatures";
import ReactJson from "react-json-view";
import {KCredit, KCreditColor} from "@kopjra/uikit/dist/src/lib/KCredit";
import {BuyCTA} from "./helpers/BuyCTA";

export interface StateProps {
    apiKeys?: Credential[];
    signatures?: HtmlSignature[];
    total: number;
    stats?: ProductCredits;
    json?: object;
    isAllowed: boolean;
}

export interface DispatchProps {
    onGetHtmlSignatures: (q: GetParams) => Promise<void>;
    onGoToApiKeys: () => void;
    onGoToDocumentation: () => void;
    onDownloadHtmlXML: (sig: HtmlSignature) => Promise<void>;
    onGetHtmlJson: (sig: HtmlSignature) => Promise<void>;
    onCleanJson: () => void;
    onGetUserCredits: () => Promise<void>;
    onResetHtmlSignatures: () => void;
}

export interface InnerProps {
}

export type Props = StateProps & DispatchProps & InnerProps;

const tableName = "html_signatures";

export const HtmlInteractions: React.FC<Props> = ({onResetHtmlSignatures, onGetUserCredits, isAllowed, onGetHtmlJson, json, onCleanJson, onDownloadHtmlXML, stats, onGetHtmlSignatures, signatures, total, onGoToApiKeys, onGoToDocumentation, apiKeys}) => {
    useEffect(() => {
        (async () => {
            await onGetHtmlSignatures(tableGetParams(tableName));
        })().catch();
    }, [onGetHtmlSignatures]);

    const actions: ActionType<HtmlSignature>[] = [
        {
            name: <div className="nowrap"><i className="fal fa-info-circle"/> <Translate value="interaction.table.details"/></div>,
            handler: (datum) => onGetHtmlJson(datum),
            shouldRender: (datum) => datum.currentStatus === InteractionStatus.Concluded,
        },
        {
            name: <div className="nowrap"><i className="fal fa-download"/> <Translate value="interaction.table.download"/></div>,
            handler: (datum) => onDownloadHtmlXML(datum),
            shouldRender: (datum) => datum.currentStatus === InteractionStatus.Concluded && !datum.deletedAt,
        }
    ];

    return (
        <KContainer className="text-center">
            {(apiKeys && signatures && stats) ? (signatures.length  || tableHasFilter(tableName)) ? (
                <KCard header={<><Translate value="interaction.htmlTitle"/></>} actions={[
                    {element: <i className="fal fa-redo"/>, action: async () => {onResetHtmlSignatures(); await Promise.all([onGetUserCredits(), onGetHtmlSignatures(tableGetParams(tableName))]);}}
                ]}>
                    <Row>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.availableQty === -1} remaining={stats.availableQty} total={stats.subscribedQty} labelText={<Translate value="interaction.available"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit infinite={stats.consumedQtyExtra === -1} remaining={stats.consumedQtyExtra} labelText={<Translate value="interaction.overQuota"/>}/>
                        </Col>
                        <Col md={3} className="text-center">
                            <KCredit color={KCreditColor.SUCCESS} infinite={stats.consumedTotal === -1} remaining={stats.consumedTotal} labelText={<Translate value="interaction.totalUsed"/>}/>
                        </Col>
                    </Row>
                    {stats.availableQty < 1 && stats.availableQty !== -1 ? (
                        <>
                            <KSpace spaces={1}/>
                            <Row>
                                <Col md={6}>
                                    <p><Alert variant={"warning"}><Translate value={"interaction.empty"} dangerousHTML={true}/></Alert></p>
                                </Col>
                            </Row>
                        </>
                    ) : null}
                    <KSpace spaces={2}/>
                    <Table
                        id={tableName}
                        data={signatures}
                        total_count={total}
                        loaderFunc={async (q: GetParams) => onGetHtmlSignatures(q)}
                        keyField={"id"}
                        waiter={<KSpinner size={KSpinnerSize.xl}/>}
                        filterDefinition={[
                            new FreeTextFilter("oURL", <Translate value="interaction.table.filter.oUrl"/>),
                            new FixedSelectionFilter("currentStatus", <Translate value="interaction.table.filter.currentStatus"/>,
                                Object.values(AppStatus).map((appStatus) => ({
                                    label: I18n.t(`interaction.table.appStatus.${appStatus}` as any),
                                    value: AppStatusToInteractionStatusMap[appStatus],
                                })),
                            ),
                            new FreeTextFilter("tagKey", <Translate value="interaction.table.filter.tagKey"/>),
                            new FreeTextFilter("tagValue", <Translate value="interaction.table.filter.tagValue"/>),
                        ]}
                    >
                        <Column colid={"id"} type={ColumnType.GENERIC} name="ID" sort="id" colspan={1} classes="text-start"
                                render={(datum: HtmlSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.id}</span>}
                        />
                        <Column colid={"createdAt"} type={ColumnType.GENERIC} name="interaction.table.createdAt" sort="createdAt" colspan={2} classes="text-left"
                                render={(datum: HtmlSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.createdAt && <Localize value={datum.createdAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                        <Column colid={"oURL"} type={ColumnType.GENERIC} name="interaction.table.oURL" sort="oURL" colspan={3} classes="text-left"
                                render={(datum: HtmlSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.oURL}</span>}
                        />
                        <Column colid={"currentStatus"} type={ColumnType.GENERIC} name="interaction.table.currentStatus" sort="currentStatus" colspan={1} classes="text-left nowrap"
                                render={(datum: HtmlSignature) => {
                                    const appStatus = InteractionStatusAppStatusMap[datum.currentStatus];
                                    return <>
                                        <i className={`fal fa-${AppStatusToIconMap[appStatus]} text-${AppStatusToColorMap[appStatus]}`}/>
                                        <Translate className={`tableStatus ${datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}`} value={`interaction.table.appStatus.${appStatus}` as any}/>
                                    </>
                                }}
                        />
                        <Column colid={"userLandedAt"} type={ColumnType.GENERIC} name="interaction.table.userLandedAt" sort="userLandedAt" colspan={2} visible={false} classes="text-left"
                                render={(datum: HtmlSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.userLandedAt && <Localize value={datum.userLandedAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                        <Column colid={"userTookoffAt"} type={ColumnType.GENERIC} name="interaction.table.userTookoffAt" sort="userTookoffAt" colspan={2} visible={false} classes="text-left"
                                render={(datum: HtmlSignature) => <span className={datum.deletedAt ? "text-decoration-line-through text-lightish" : ""}>{datum.userTookoffAt && <Localize value={datum.userTookoffAt} dateFormat="kptable.date.long"/>}</span>}
                        />
                        <Column colid="actions" type={ColumnType.ACTIONS} actions={actions} colspan={3} classes="text-right"/>
                    </Table>
                </KCard>
            ) : isAllowed ? (
                <>
                    <KSpace spaces={3}/>
                    <img src={IMAGES["web-trust"].still.large.dark} height={165} alt={"Web Trust"}/>
                    <KSpace/>
                    <h4><Translate value="interaction.noInteraction"/></h4>
                    <KSpace spaces={2}/>
                    {apiKeys.length ? (
                        <h4><Translate value="interaction.withKeys"/></h4>
                    ) : (
                        <h4><Translate value="interaction.noKeys"/></h4>
                    )}
                    <KSpace spaces={2}/>
                    <div>
                        {!apiKeys.length ? (
                            <>
                                <KButton
                                    text={<><i className="fal fa-angle-double-right"/> <Translate value="user.apiKeys.title"/></>}
                                    variant={KBUTTON_VARIANT.primary}
                                    size={KBUTTON_SIZE.nm}
                                    onClick={() => onGoToApiKeys()}
                                />
                                <KSpace v={true} spaces={2}/>
                            </>
                        ) : null}
                        <KButton
                            text={<><i className="fal fa-angle-double-right"/> <Translate value="user.documentation"/></>}
                            variant={KBUTTON_VARIANT.primary}
                            size={KBUTTON_SIZE.nm}
                            onClick={() => onGoToDocumentation()}
                        />
                    </div>
                    {/*<KSpace spaces={2}/>*/}
                    {/*<h4><a className="link" href={`mailto:info@kopjra.com?subject=${I18n.t("home.contactSubject")}&body=${I18n.t("home.contactBody")}`} target="_blank" rel="noopener noreferrer"><Translate value="home.contactus"/></a></h4>*/}
                </>
            ) : (
                <BuyCTA/>
            ) : (
                <KCard header={<Translate value="interaction.htmlTitle"/>}>
                    <KTableLoader/>
                </KCard>
            )}
            <KModal show={!!json} header={<Translate value={"interaction.table.details"}/>}
                    onHide={() => onCleanJson()} variant={KModalVariant.secondary} size={KModalSize.lg} buttonText={<Translate value="generic.close"/>}>
                {json ? (
                    <div style={{width: "100%", overflowX: "auto"}}>
                        <ReactJson src={json} collapsed={1} collapseStringsAfterLength={15} displayObjectSize={false}/>
                    </div>
                ) : null}
            </KModal>
        </KContainer>
    );
}
